import type { FC } from "react";
import { useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import type { IncludeExcludeKeywords } from "app/pages/digital-marketing/brand-monitoring-campaigns/shared/useBPRequestBody";
import { MetricTitle } from "app/pages/keyword-analysis/TotalTrafficPage/Components/UtilityComponents";
import { ETiers, GRAPH_API } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/Constants";
import type {
    RankingDistributionSingleModeData,
    RankingDistributionPayload,
} from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/rankingDistribution.types";
import { RankingDistributionTableSummary } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/RankingDistributionTableSummary";
// eslint-disable-next-line max-len
import { RankingDistributionTableTitleContainer } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/RankingDistributionTableTitleContainer";
import {
    Container,
    TopSection,
    TableTopContainer,
    MetricTitleWrapper,
    HorizontalLine,
    MetricsBarsContainer,
} from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/StyledComponents";
// eslint-disable-next-line max-len
import { useRankingDistributionTableTopContext } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/useRankingDistributionTableTopContext";
import {
    getAggregatedTiers,
    getSingleTiers,
} from "app/pages/website-analysis/traffic-sources/search/components/filters/RankingTierFilterV2";
import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import MetricsCompareBars from "app/components/React/MetricsCompareBars/MetricsCompareBars";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { CompareBanner } from "app/pages/website-analysis/components/compare-banner/CompareBanner";
import type { IDurationData } from "app/services/DurationService";
import DurationService from "app/services/DurationService";
import { DefaultFetchService, requestParams } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import type { IButtonProps } from "./RankingDistributionTableTop";
import {
    getBoxSubtitlesFilters,
    getMetricsCompareBarItems,
    transformRankingDistributionPayloadToRankingDistributionData,
} from "./Utilities";

// determines which legends and tiers are displayed in the chart.
// This is necessary because the table filter for the chart will
// remove legends and tiers that are not chosen.
export const initialDisplayedTiers = [ETiers.TIER1, ETiers.TIER2, ETiers.TIER3, ETiers.TIER4, ETiers.TIER5];

const tooltipTitle = "rankings.distribution.tooltip.title";
const tooltipSubTitles = [
    "rankings.distribution.tooltip.subtitles.group",
    "rankings.distribution.tooltip.subtitles.group.share",
];

interface IRankingDistributionSingleGraphInnerProps {
    data?: RankingDistributionSingleModeData;
    filters: Record<string, string>;
    isLoading: boolean;
    displayedTiers?: ETiers[];
    metricTitle: string;
    durationData: IDurationData;
    translatedWords: {
        metricTitle: string;
        tooltipTitle: string;
        metricSubTitles: string[];
    };
}

const RankingDistributionSingleGraphInner: FC<IRankingDistributionSingleGraphInnerProps> = (props) => {
    const { data, isLoading, filters, displayedTiers, translatedWords, durationData } = props;
    const { country, webSource } = filters;
    const { metricSubTitles, metricTitle, tooltipTitle } = translatedWords;

    return (
        <Container>
            <TopSection>
                <MetricTitleWrapper>
                    <MetricTitle headline={metricTitle} />
                </MetricTitleWrapper>
                <BoxSubtitle filters={getBoxSubtitlesFilters({ durationData, webSource, country })} />
            </TopSection>
            <HorizontalLine />
            <MetricsBarsContainer className="metrics-bars-container">
                <MetricsCompareBars
                    // @ts-expect-error Types of property 'width' are incompatible.
                    items={getMetricsCompareBarItems(data) || []}
                    title={tooltipTitle}
                    subTitles={metricSubTitles}
                    isFetching={isLoading}
                    minWidthToShowData={0.001}
                    minWidthToShowDataMiddle={0.01}
                />
            </MetricsBarsContainer>
            <RankingDistributionTableSummary
                data={data}
                isLoading={isLoading}
                displayedTiers={displayedTiers}
                month={durationData.forWidget as string}
            />
        </Container>
    );
};

interface IRankingDistributionSingleMode {
    dataParamsAdapter: (params: Record<string, unknown>, table: boolean) => Record<string, unknown>;
    metricTitle?: string;
    buttonsProps: IButtonProps;
    requestBody?: IncludeExcludeKeywords[];
}

const RankingDistributionSingleGraphContainer: FC<IRankingDistributionSingleMode> = (props) => {
    const { tableFilters } = useRankingDistributionTableTopContext();
    const { duration, webSource, country, ranking } = tableFilters;
    // this state is for filtering the legends based on the table filter for position.
    const [displayedTiers, setDisplayedTiers] = useState(initialDisplayedTiers);
    const params = props.dataParamsAdapter({ ...tableFilters }, false);
    const translate = useTranslation();

    const paramsAsString = requestParams(params);

    const { data, isLoading } = useQuery({
        queryKey: [GRAPH_API, duration, webSource, country, ranking, tableFilters],
        queryFn: async () =>
            DefaultFetchService.getInstance().post<RankingDistributionPayload | undefined>(
                `${GRAPH_API}?${paramsAsString}`,
                props.requestBody,
            ),
        select: (response) => transformRankingDistributionPayloadToRankingDistributionData(response, duration),
    });

    const getTiersFromRankingProp = (ranking): ETiers[] => {
        let rank = ranking;
        if (ranking.split(":").length > 1) {
            rank = ranking.split(":")[1];
        }
        const allTiers = [...getSingleTiers(), ...getAggregatedTiers()];
        return allTiers.filter((tier) => tier.value === rank)[0].tiers;
    };

    useEffect(() => {
        if (!ranking) {
            setDisplayedTiers(initialDisplayedTiers);
            return;
        }
        // use ranking prop to calculate displayed tiers value
        const tiersToDisplay = getTiersFromRankingProp(ranking);
        setDisplayedTiers(tiersToDisplay);
    }, [ranking]);

    const durationData = DurationService.getDurationData(duration);
    const bannerProps = {
        domain: params.keys as string,
        title: translate("ranking.distribution.compare.banner.title"),
        buttonText: translate("ranking.distribution.compare.banner.button.text"),
        trackClickCallback: () => {
            allTrackers.dispatchTrackEvent(
                "ranking.distribution.compare.my_project_banner",
                "click",
                "ranking.distribution.compare.my_project_banner",
            );
        },
    };

    return (
        <>
            <RankingDistributionSingleGraphInner
                metricTitle={props.metricTitle}
                displayedTiers={displayedTiers}
                data={data}
                filters={{ duration, ranking, country, webSource }}
                isLoading={isLoading}
                durationData={durationData}
                translatedWords={{
                    metricTitle: translate(props.metricTitle),
                    tooltipTitle: translate(tooltipTitle, {
                        month: durationData.forWidget as string,
                    }),
                    metricSubTitles: tooltipSubTitles.map((tooltipSubtitle) => translate(tooltipSubtitle)),
                }}
            />
            <CompareBanner {...bannerProps} />
            <TableTopContainer>
                <RankingDistributionTableTitleContainer
                    {...props.buttonsProps}
                    requestBody={props.requestBody}
                    duration={duration}
                />
            </TableTopContainer>
        </>
    );
};

export const RankingDistributionSingleGraph = RankingDistributionSingleGraphContainer;
